<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">倉庫管理</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openCreateModal">
                <i class="ri-add-line"></i> 新增倉庫
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filters.warehouseType" @change="fetchWarehouses" class="form-control">
          <option value="">全部類型</option>
          <option value="main">總倉</option>
          <option value="sub">分倉</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.status" @change="fetchWarehouses" class="form-control">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
      </div>
      <div class="search-box">
        <input v-model="filters.search" @input="onSearch" placeholder="搜尋倉庫名稱或代碼" class="form-control" />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>倉庫代碼</th>
            <th>倉庫名稱</th>
            <th>類型</th>
            <th>聯絡人</th>
            <th>聯絡電話</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="7" class="text-center">載入中...</td>
          </tr>
          <tr v-else-if="!warehouses.length">
            <td colspan="7" class="text-center text-muted">尚無倉庫資料</td>
          </tr>
          <tr v-for="w in warehouses" :key="w.id">
            <td><code>{{ w.warehouseCode }}</code></td>
            <td>{{ w.warehouseName }}</td>
            <td>
              <span :class="['badge', w.warehouseType === 'main' ? 'badge-primary' : 'badge-secondary']">
                {{ w.warehouseType === 'main' ? '總倉' : '分倉' }}
              </span>
            </td>
            <td>{{ w.contactName || '-' }}</td>
            <td>{{ w.contactPhone || '-' }}</td>
            <td>
              <span :class="['badge', w.status === 'active' ? 'badge-success' : 'badge-danger']">
                {{ w.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <button class="btn btn-sm btn-outline" @click="openEditModal(w)">編輯</button>
              <button
                v-if="w.warehouseType !== 'main'"
                class="btn btn-sm"
                :class="w.status === 'active' ? 'btn-outline-danger' : 'btn-outline-success'"
                @click="toggleStatus(w)"
                style="margin-left:6px"
              >
                {{ w.status === 'active' ? '停用' : '啟用' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 新增/編輯 Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editingId ? '編輯倉庫' : '新增倉庫' }}</h3>
          <button class="modal-close" @click="closeModal"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label required">倉庫代碼</label>
            <input v-model="form.warehouseCode" :disabled="!!editingId" class="form-control" placeholder="例：WH-001" />
          </div>
          <div class="form-group">
            <label class="form-label required">倉庫名稱</label>
            <input v-model="form.warehouseName" class="form-control" placeholder="例：台北總倉" />
          </div>
          <div class="form-group">
            <label class="form-label required">類型</label>
            <select v-model="form.warehouseType" :disabled="!!editingId" class="form-control">
              <option value="main">總倉</option>
              <option value="sub">分倉</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">地址</label>
            <input v-model="form.address" class="form-control" placeholder="倉庫地址" />
          </div>
          <div class="form-group">
            <label class="form-label">聯絡人</label>
            <input v-model="form.contactName" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label">聯絡電話</label>
            <input v-model="form.contactPhone" class="form-control" />
          </div>
          <div class="form-group">
            <label class="form-label">備註</label>
            <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">排序</label>
            <input v-model.number="form.sortOrder" type="number" class="form-control" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveWarehouse">
            {{ saving ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { warehouseService } from '@/api/modules/warehouses.js';

const warehouses = ref([]);
const loading = ref(false);
const saving = ref(false);
const showModal = ref(false);
const editingId = ref(null);

const filters = ref({ warehouseType: '', status: 'active', search: '' });
const form = ref({
  warehouseCode: '', warehouseName: '', warehouseType: 'sub',
  address: '', contactName: '', contactPhone: '', notes: '', sortOrder: 0
});

let searchTimer = null;

async function fetchWarehouses() {
  loading.value = true;
  try {
    const params = {};
    if (filters.value.warehouseType) params.warehouseType = filters.value.warehouseType;
    if (filters.value.status) params.status = filters.value.status;
    if (filters.value.search) params.search = filters.value.search;
    const res = await warehouseService.getWarehouses(params);
    warehouses.value = res.data?.items || [];
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

function onSearch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(fetchWarehouses, 400);
}

function openCreateModal() {
  editingId.value = null;
  form.value = { warehouseCode: '', warehouseName: '', warehouseType: 'sub', address: '', contactName: '', contactPhone: '', notes: '', sortOrder: 0 };
  showModal.value = true;
}

function openEditModal(w) {
  editingId.value = w.id;
  form.value = {
    warehouseCode: w.warehouseCode,
    warehouseName: w.warehouseName,
    warehouseType: w.warehouseType,
    address: w.address || '',
    contactName: w.contactName || '',
    contactPhone: w.contactPhone || '',
    notes: w.notes || '',
    sortOrder: w.sortOrder || 0
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function saveWarehouse() {
  if (!form.value.warehouseCode || !form.value.warehouseName) {
    alert('請填寫倉庫代碼與名稱');
    return;
  }
  saving.value = true;
  try {
    if (editingId.value) {
      await warehouseService.updateWarehouse(editingId.value, form.value);
    } else {
      await warehouseService.createWarehouse(form.value);
    }
    closeModal();
    fetchWarehouses();
  } catch (e) {
    alert(e.response?.data?.message || '儲存失敗');
  } finally {
    saving.value = false;
  }
}

async function toggleStatus(w) {
  const newStatus = w.status === 'active' ? 'inactive' : 'active';
  const label = newStatus === 'active' ? '啟用' : '停用';
  if (!confirm(`確定要${label}「${w.warehouseName}」嗎？`)) return;
  try {
    await warehouseService.updateWarehouseStatus(w.id, newStatus);
    fetchWarehouses();
  } catch (e) {
    alert(e.response?.data?.message || '操作失敗');
  }
}

onMounted(fetchWarehouses);
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table { min-width: 800px; }

.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal-box {
  background: #fff; border-radius: 12px; width: 480px; max-width: 95vw;
  box-shadow: 0 8px 32px rgba(0,0,0,.15);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #eee;
}
.modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
.modal-close { background: none; border: none; cursor: pointer; font-size: 20px; color: #999; }
.modal-body { padding: 20px; max-height: 60vh; overflow-y: auto; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid #eee; }
.form-group { margin-bottom: 14px; }
.form-label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 4px; color: #555; }
.form-label.required::after { content: ' *'; color: #e74c3c; }
.form-control { width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.badge { padding: 2px 8px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.badge-primary { background: #e3f0ff; color: #1a73e8; }
.badge-secondary { background: #f0f0f0; color: #666; }
.badge-success { background: #e6f9ee; color: #27ae60; }
.badge-danger { background: #fde8e8; color: #e74c3c; }
.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-outline { background: transparent; border: 1px solid #1a73e8; color: #1a73e8; }
.btn-outline-danger { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.btn-outline-success { background: transparent; border: 1px solid #27ae60; color: #27ae60; }
.btn-sm { padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
</style>
